import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { ResultCard } from "../components/ResultCard";
import { Results } from "../components/Paged";
import { ChipSelect, inputClass, labelClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import {
  listLoads, rateLabel, ratePerMile, laneLabel,
  CATEGORY_OPTIONS, EQUIPMENT_TYPE_OPTIONS, FULL_PARTIAL_OPTIONS,
  LOAD_POST_TYPES, LOAD_BID_TYPES, type LoadSummary,
} from "../lib/loads";

export function LoadBoard() {
  const { active } = useProfiles();
  const [params] = useSearchParams();
  const isPoster = active ? LOAD_POST_TYPES.includes(active.type) : false;
  const isBidder = active ? LOAD_BID_TYPES.includes(active.type) : false;

  // Initial filters come from the URL (e.g. a marketing deep link
  // /loads?category=vehicle or ?equipment=reefer), so the first search is
  // pre-filtered to what the visitor expected.
  const [loads, setLoads] = useState<LoadSummary[]>([]);
  const [q, setQ] = useState(params.get("q") ?? "");
  const [origin, setOrigin] = useState(params.get("origin") ?? "");
  const [originRadius, setOriginRadius] = useState(params.get("origin_radius") ?? "");
  const [destination, setDestination] = useState(params.get("destination") ?? "");
  const [category, setCategory] = useState(params.get("category") ?? "");
  const [equipment, setEquipment] = useState(params.get("equipment") ?? "");
  const [fullPartial, setFullPartial] = useState(params.get("full_partial") ?? "");
  const [loading, setLoading] = useState(true);

  async function search() {
    setLoading(true);
    try {
      setLoads(
        (await listLoads({
          q: q || undefined,
          origin: origin || undefined,
          origin_radius: originRadius ? Number(originRadius) : undefined,
          destination: destination || undefined,
          category: category || undefined,
          equipment: equipment || undefined,
          full_partial: fullPartial || undefined,
        })).loads,
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCard = (l: (typeof loads)[number]) => (
    <ResultCard
      key={l.id}
      kind="loads"
      id={l.id}
      to={`/loads/${l.id}`}
      title={laneLabel(l.origin, l.destination)}
      verified
      meta={[l.poster_name, l.origin_distance_mi != null ? `${l.origin_distance_mi} mi away` : null, l.category ? labelize(l.category) : null, l.commodity].filter(Boolean).join(" · ")}
      tags={[l.equipment_type, l.full_partial, l.category]}
      specs={[
        ["Rate", rateLabel(l.rate, l.rate_type)],
        ["Rate/mi", ratePerMile(l.rate, l.rate_type, l.trip_miles)],
        ["Weight", l.weight ? `${l.weight.toLocaleString()} lb` : ""],
        ["Distance", l.trip_miles ? `${l.trip_miles.toLocaleString()} mi` : ""],
        ["Pickup", l.pickup_date],
      ]}
    />
  );

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Load board"
        subtitle="Book freight from verified shippers — bid your rate."
        actions={
          <>
            {isPoster && (
              <>
                <Link to="/loads/new"><Button>Post a shipment</Button></Link>
                <Link to="/loads/mine"><Button variant="ghost">My loads</Button></Link>
              </>
            )}
            {isBidder && <Link to="/bids"><Button variant="ghost">My bids</Button></Link>}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        {/* Filters rail */}
        <aside className="grid h-max content-start gap-4 rounded-card border border-line bg-surface p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-muted">Filters</p>
          <label className={labelClass}>
            Origin
            <input className={inputClass} placeholder="City, ST" value={origin} onChange={(e) => setOrigin(e.target.value)} />
          </label>
          <label className={labelClass}>
            Distance
            <select className={inputClass} value={originRadius} onChange={(e) => setOriginRadius(e.target.value)} aria-label="Search radius">
              <option value="">Any distance</option>
              <option value="50">Within 50 mi</option>
              <option value="100">Within 100 mi</option>
              <option value="250">Within 250 mi</option>
              <option value="500">Within 500 mi</option>
            </select>
          </label>
          <label className={labelClass}>
            Destination
            <input className={inputClass} placeholder="City, ST" value={destination} onChange={(e) => setDestination(e.target.value)} />
          </label>
          <ChipSelect label="Category" options={CATEGORY_OPTIONS} value={category} onChange={setCategory} />
          <ChipSelect label="Equipment" options={EQUIPMENT_TYPE_OPTIONS} value={equipment} onChange={setEquipment} />
          <ChipSelect label="Load" options={FULL_PARTIAL_OPTIONS} value={fullPartial} onChange={setFullPartial} />
          <Button onClick={search}>Apply filters</Button>
        </aside>

        {/* Results */}
        <div className="grid content-start gap-4">
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void search();
            }}
          >
            <input className={`flex-1 ${inputClass}`} placeholder="Search lane, commodity, or reference" value={q} onChange={(e) => setQ(e.target.value)} />
            <Button type="submit">Search</Button>
          </form>

          {loading ? (
            <p className="font-mono text-xs text-muted">Searching…</p>
          ) : (
            <Results
              items={loads}
              perPage={10}
              defaultView="list"
              gridClass="grid gap-4 sm:grid-cols-2"
              renderCard={(l) => loadCard(l)}
              renderRow={(l) => loadCard(l)}
              empty={<p className="font-mono text-sm text-muted">No open shipments match. Try widening your filters.</p>}
            />
          )}
        </div>
      </div>
    </div>
  );
}
